import { useCallback, useEffect, useState } from "react";
import type {
  ConfirmationQueueEntryV1,
  ImportDraftReviewViewV1,
  SubmitImportSuggestionDecisionV1,
} from "@enrein/shared-contracts";
import type { StudioApiClient } from "../../../api.js";
import { V10Btn } from "../components/V10Primitives.js";

export function V10ImportConfirmationPanel({
  api,
  onQueueChanged,
}: {
  readonly api: StudioApiClient;
  readonly onQueueChanged?: () => void;
}) {
  const [entries, setEntries] = useState<ConfirmationQueueEntryV1[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [review, setReview] = useState<ImportDraftReviewViewV1 | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [decisions, setDecisions] = useState<Record<string, SubmitImportSuggestionDecisionV1>>({});

  const refreshQueue = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const queue = await api.listImportConfirmationQueue();
      setEntries(queue.entries);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setLoading(false);
    }
  }, [api]);

  const loadReview = useCallback(
    async (importDraftId: string) => {
      setBusy(true);
      setError(null);
      try {
        const view = await api.getImportDraftReview(importDraftId);
        setReview(view);
        setDecisions({});
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : String(caught));
        setReview(null);
      } finally {
        setBusy(false);
      }
    },
    [api],
  );

  useEffect(() => {
    void refreshQueue();
  }, [refreshQueue]);

  useEffect(() => {
    if (!selectedId) {
      setReview(null);
      return;
    }
    void loadReview(selectedId);
  }, [loadReview, selectedId]);

  async function submitDecisions() {
    if (!selectedId || !review) return;
    const payload = Object.values(decisions);
    if (payload.length === 0) {
      setError("Select at least one decision.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.submitImportDraftDecisions(selectedId, {
        schemaId: "importConfirmation.submitDecisionsRequest.v1",
        decisions: payload,
      });
      setMessage("Decisions saved.");
      await loadReview(selectedId);
      await refreshQueue();
      onQueueChanged?.();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  }

  async function applyToDraft() {
    if (!selectedId) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.applyImportConfirmationToDraft(selectedId);
      setMessage(`Applied ${result.appliedFieldCount} field(s) to draft ${result.appliedDraftId}.`);
      await refreshQueue();
      await loadReview(selectedId);
      onQueueChanged?.();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="v10-import-confirmation">
      <p className="v10-cl-kicker">Import confirmation queue</p>
      {loading ? <p className="v10-muted">Loading queue…</p> : null}
      {!loading && entries.length === 0 ? (
        <p className="v10-muted">No import drafts awaiting confirmation.</p>
      ) : (
        <ul className="v10-import-queue">
          {entries.map((entry) => (
            <li key={entry.importDraftId}>
              <button
                type="button"
                className={selectedId === entry.importDraftId ? "v10-import-queue-item is-active" : "v10-import-queue-item"}
                onClick={() => setSelectedId(entry.importDraftId)}
              >
                <span>{entry.sourceUrl}</span>
                <span className="v10-pill">
                  {entry.pendingDecisionCount > 0 ? `${entry.pendingDecisionCount} pending` : entry.reviewStatus}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {review ? (
        <div className="v10-import-review">
          <p className="v10-muted">{review.sourceUrl}</p>
          <ul className="v10-import-suggestions">
            {review.items.map(({ suggestion, decision }) => (
              <li key={suggestion.suggestionId}>
                <div>
                  <strong>{suggestion.fieldKind}</strong>
                  <p>{suggestion.value}</p>
                  {decision ? <p className="v10-muted">Saved: {decision.decision}</p> : null}
                </div>
                {!decision ? (
                  <div className="v10-hero-actions">
                    {(["CONFIRMED", "REJECTED", "DEFERRED"] as const).map((kind) => (
                      <V10Btn
                        key={kind}
                        variant="line"
                        disabled={busy}
                        onClick={() =>
                          setDecisions((current) => ({
                            ...current,
                            [suggestion.suggestionId]: {
                              schemaId: "importConfirmation.submitDecision.v1",
                              suggestionId: suggestion.suggestionId,
                              decision: kind,
                            },
                          }))
                        }
                      >
                        {kind.toLowerCase()}
                      </V10Btn>
                    ))}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
          <div className="v10-hero-actions">
            <V10Btn variant="gold" disabled={busy} onClick={() => void submitDecisions()}>
              Save decisions
            </V10Btn>
            <V10Btn variant="line" disabled={busy} onClick={() => void applyToDraft()}>
              Apply to Business Profile draft
            </V10Btn>
          </div>
        </div>
      ) : null}

      {message ? <p className="v10-muted">{message}</p> : null}
      {error ? <p className="v10-validation">{error}</p> : null}
    </div>
  );
}
